import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { OnboardingService } from '@app/shared/services/onboarding.service';

/** Seconds the coach has to look at the session (tab in front) before the question appears. */
export const FIRST_SESSION_FEEDBACK_DELAY_S = 20;
/** How long the thank-you stays before the card goes away. */
export const FIRST_SESSION_FEEDBACK_THANKS_MS = 2500;
/**
 * On this device: the sessions the coach already answered or closed the question for, as a JSON
 * list (suffixed with their id).
 */
export const FIRST_SESSION_FEEDBACK_CLOSED_KEY = 'saideline.firstSessionFeedback.closed';
/** The question starts with the coach's second session: the first belongs to the co-coach's tour. */
export const FIRST_SESSION_FEEDBACK_FROM_SESSION = 2;
/** How many handled sessions are remembered per coach; older ones drop out. */
const HANDLED_SESSIONS_KEPT = 200;

/**
 * "Do you like this session?" with thumbs up and down, whenever a coach looks at a session for a
 * while: every coach from their second session on, every session, once per session. The answers go to
 * the admin insights.
 */
@Component({
    selector: 'app-first-session-feedback',
    templateUrl: './first-session-feedback.component.html',
    styleUrl: './first-session-feedback.component.scss',
    standalone: false,
})
export class FirstSessionFeedbackComponent implements OnChanges, OnDestroy {
    /** The session on the page; the question is about this one. */
    @Input() sessionId: string | null | undefined;

    state: 'hidden' | 'asking' | 'thanks' = 'hidden';

    private secondsViewed = 0;
    private viewTimer?: ReturnType<typeof setInterval>;
    private thanksTimer?: ReturnType<typeof setTimeout>;

    constructor(
        private readonly authService: AuthApiService,
        private readonly onboardingService: OnboardingService
    ) {}

    /** A new session on the page (e.g. regenerated) starts the count again. */
    ngOnChanges(): void {
        this.stopViewTimer();
        this.secondsViewed = 0;
        if (this.state === 'asking') {
            this.state = 'hidden';
        }
        if (!this.isDue()) {
            return;
        }
        // Only time with the page in front counts as looking at the session.
        this.viewTimer = setInterval(() => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            this.secondsViewed += 1;
            if (this.secondsViewed >= FIRST_SESSION_FEEDBACK_DELAY_S) {
                this.stopViewTimer();
                if (this.isDue()) {
                    this.state = 'asking';
                }
            }
        }, 1000);
    }

    answer(liked: boolean): void {
        const sessionId = this.sessionId;
        if (this.state !== 'asking' || !sessionId) {
            return;
        }
        this.state = 'thanks';
        this.markHandled(sessionId);
        this.onboardingService.rateSession(liked, sessionId).subscribe({ error: () => {} });
        this.thanksTimer = setTimeout(() => (this.state = 'hidden'), FIRST_SESSION_FEEDBACK_THANKS_MS);
    }

    /** Closed without an answer: not asked again for this session on this device. */
    close(): void {
        if (this.sessionId) {
            this.markHandled(this.sessionId);
        }
        this.state = 'hidden';
    }

    ngOnDestroy(): void {
        this.stopViewTimer();
        clearTimeout(this.thanksTimer);
    }

    /**
     * A signed-in coach with at least two sessions, on a session they have not answered or closed the
     * question for yet.
     */
    private isDue(): boolean {
        const sessions = this.authService.currentUser?.stats?.totalSessions ?? 0;
        return (
            sessions >= FIRST_SESSION_FEEDBACK_FROM_SESSION && !!this.sessionId && !this.handled().includes(this.sessionId)
        );
    }

    private storageKey(): string | null {
        const user = this.authService.currentUser;
        return user ? `${FIRST_SESSION_FEEDBACK_CLOSED_KEY}.${user.sub}` : null;
    }

    private handled(): string[] {
        const key = this.storageKey();
        try {
            const parsed = key ? JSON.parse(localStorage.getItem(key) ?? '[]') : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private markHandled(sessionId: string): void {
        const key = this.storageKey();
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, JSON.stringify([...this.handled(), sessionId].slice(-HANDLED_SESSIONS_KEPT)));
        } catch {
            // Storage blocked: the question may come back next visit
        }
    }

    private stopViewTimer(): void {
        clearInterval(this.viewTimer);
        this.viewTimer = undefined;
    }
}
